<template>
  <div class="auto-rule">

    <div v-if="!hasTriggerComponent && editing">
      <!-- Add Trigger Empty Slot -->
      <div class="auto-comp-container">
        <div class="auto-comp" @click="addTriggerClicked">
          <div class="auto-comp-icon auto-comp-empty-trigger">
            <i class="fas fa-fw fa-plus-circle"></i>
          </div>
          <div class="auto-comp-info">
            <div class="title">Add Triger</div>
            <div class="description automation-field-description">Trigger required to start the rule</div>
          </div>
        </div>
      </div>

    </div>

    <AutomationRuleComponent
      v-for="(component, index) of rootComponents"
      :key="component.id"
      :editing="editing"
      :autoRuleComponentList="autoRuleComponents"
      :autoRuleComponent="component"
      :firstChild="index == 0"
      :lastChild="index == rootComponents.length-1"
      @componentClicked="componentClicked"
      @deleteClicked="componentDeleteClicked"
      @insertClicked="componentInsertClicked"
      @insertChildClicked="componentInsertChildClicked"
      @addComponent="addComponentClicked"
    />
  </div>
</template>


<script>
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import AutomationMixin from '@/modules/automations/mixins/automations'
import AutomationRuleComponent from '@/modules/automations/components/automationRuleComponent'
import AutomationRuleAddComponent from '@/modules/automations/components/automationRuleAddComponent'

import AutoRuleComponentDialog from '@/modules/automations/views/autoRuleComponentDialog'

import { AutomationComponentTypes, AutomationTriggerTypes } from '@/store/types'

export default {
  name: 'AutomationRuleTree',
  model: {
    event: 'update'
  },
  mixins: [AutomationMixin],
  components: {
    AutomationRuleComponent,
    AutomationRuleAddComponent
  },
  props: {
    value: Array,
    editing: Boolean
  },
  computed: {
    hasTriggerComponent() {
      return this.rootComponents.some(comp => comp.autoComponentType.type == AutomationComponentTypes.trigger)
    },
    rootComponents() {
      return this.autoRuleComponents.filter(comp => !comp.parentAutoRuleComponentId)
    }
  },
  watch: {
    value: function() {
      this.autoRuleComponents = _.cloneDeep(this.value || [])
    }
  },
  data() {
    return {
      autoRuleComponents: [],
    }
  },
  methods: {
    addTriggerClicked() {
      this.displayModal(AutoRuleComponentDialog, {
        isCreateMode: true,
        requiredComponentTypes: [AutomationComponentTypes.trigger],
        editing: true
      })
      .then(ruleComponent => {
        if(ruleComponent) {
          this.addComponentToRule(null, ruleComponent, 0)
          this.componentListUpdated()
        }
      })
    },
    addComponentToRule(parentAutoRuleComponentId, ruleComponent, insertIndex = -1) {
      ruleComponent.parentAutoRuleComponentId = parentAutoRuleComponentId
      ruleComponent.id = `temp-id-` + uuidv4()
      ruleComponent.isNewComponent = true

      if(insertIndex >= 0)
        this.autoRuleComponents.splice(insertIndex, 0, ruleComponent)
      else
        this.autoRuleComponents.push(ruleComponent)
    
    },
    addComponentClicked(parentAutoRuleComponentId, listInsertIndex = -1) {
      // Calculate available data for the new component at this point
      var availableData = this.calculateComponentDataAccess(parentAutoRuleComponentId, listInsertIndex)

      this.displayModal(AutoRuleComponentDialog, {
        isCreateMode: true,
        requiredComponentTypes: [AutomationComponentTypes.condition, AutomationComponentTypes.branch, AutomationComponentTypes.action],
        editing: true,
        availableModelList: availableData.availableModelList,
        availablePlaceholderList: availableData.availablePlaceholderList,
      })
      .then(ruleComponent => {
        if(ruleComponent) {
          this.addComponentToRule(parentAutoRuleComponentId, ruleComponent, listInsertIndex)
          this.componentListUpdated()
        }
      })      
    },
    componentClicked(autoRuleComponent) {
      var availableData = this.calculateComponentDataAccess(autoRuleComponent.id)

      this.displayModal(AutoRuleComponentDialog, {
        autoRuleComponent: autoRuleComponent,
        editing: this.editing,
        availableModelList: availableData.availableModelList,
        availablePlaceholderList: availableData.availablePlaceholderList,
      })
      .then(componentData => {
        if(componentData && this.editing) {

          // Copy data over
          autoRuleComponent.triggerSettings = _.cloneDeep(componentData.triggerSettings)
          autoRuleComponent.conditionSettings = _.cloneDeep(componentData.conditionSettings)
          autoRuleComponent.branchSettings = _.cloneDeep(componentData.branchSettings)
          autoRuleComponent.actionSettings = _.cloneDeep(componentData.actionSettings)

          this.componentListUpdated()
        }
      })
    },
    componentDeleteClicked(autoRuleComponent) {
      this.confirmDeleteMessage('Delete Component?', 'This will delete the component and any child components from the automation tree')
      .then(confirm => {
        if(confirm) {
          this.removeComponent(autoRuleComponent.id)
          this.componentListUpdated()
        }
      })
    },
    removeComponent(componentId) {
      var childComponents = this.autoRuleComponents.filter(ruleComp => ruleComp.parentAutoRuleComponentId == componentId)

      this.autoRuleComponents = this.autoRuleComponents.filter(ruleComp => ruleComp.id != componentId)

      childComponents.forEach(child => {
        this.removeComponent(child.id)
      })
    },
    componentInsertClicked(autoRuleComponent) {
      let insertIndex = this.autoRuleComponents.findIndex(ruleComp => ruleComp.id == autoRuleComponent.id)
      if(insertIndex != -1) {
        this.addComponentClicked(autoRuleComponent.parentAutoRuleComponentId, insertIndex+1)
        this.componentListUpdated()
      }
    },
    componentInsertChildClicked(autoRuleComponent) {
      let insertIndex = this.autoRuleComponents.findIndex(ruleComp => ruleComp.id == autoRuleComponent.id)
      if(insertIndex != -1) {
        this.addComponentClicked(autoRuleComponent.parentAutoRuleComponentId, insertIndex)
        this.componentListUpdated()
      }
    },
    componentListUpdated() {
      this.$emit('update', this.autoRuleComponents)
    },

    // Generate the component data access tree for each component
    // Calculates the available models & placeholders for each component in the tree
    // Used for displaying placeholders when editing components
    // JB: Feels really hacky
    calculateComponentDataAccess(searchParentId, childIndex = -1, parentAutoRuleComponentId = null, dataStack = []) {
      var listCopy = _.cloneDeep(this.autoRuleComponents)
      listCopy.forEach((item, index) => {
        item.index = index
      })
      var availableData = {
        modelList: [],
        placeholderList: []
      }

      let levelList = listCopy.filter(listComp => listComp.parentAutoRuleComponentId == parentAutoRuleComponentId)
      let outputLevel = []
      
      for(let listComp of levelList) {
        let outputComp = _.cloneDeep(listComp)
        outputComp.availableModelList = []
        outputComp.availablePlaceholderList = []

        if(outputComp.autoComponentType) {
          if(_.isArray(outputComp.autoComponentType.outputTypeList)) {
            availableData.modelList = _.concat(availableData.modelList, outputComp.autoComponentType.outputTypeList)
          }
          if(_.isArray(outputComp.autoComponentType.outputPlaceholderList))
            availableData.placeholderList = _.concat(availableData.placeholderList, outputComp.autoComponentType.outputPlaceholderList) 
        }

        // Set comp data up to now in the stack
        dataStack.forEach(stackItem => {
          outputComp.availableModelList = _.concat(outputComp.availableModelList, stackItem.modelList)
          outputComp.availablePlaceholderList = _.concat(outputComp.availablePlaceholderList, stackItem.placeholderList)
        })
        outputComp.availableModelList = _.concat(outputComp.availableModelList, availableData.modelList)
        outputComp.availablePlaceholderList = _.concat(outputComp.availablePlaceholderList, availableData.placeholderList)

        // Note: outputChildTypeList for children only
        let childData = _.cloneDeep(availableData)
        childData.modelList = _.concat(availableData.modelList, outputComp.autoComponentType.outputChildTypeList || [])
        dataStack.push(childData)

        var childInfo = this.calculateComponentDataAccess(searchParentId, childIndex, outputComp.id, dataStack)
        if(childInfo)
          return childInfo

        // If same parent but no children found, just return whatever is in the data stack (so it includes child types)
        // Happens when inserting first child
        if(outputComp.id == searchParentId) {
          return {
            availableModelList: _.concat(outputComp.availableModelList, outputComp.autoComponentType.outputChildTypeList || []),
            availablePlaceholderList: outputComp.availablePlaceholderList            
          }          
        }

        dataStack.pop()
        outputLevel.push(outputComp)
      }

      if(outputLevel.length > 0 && outputLevel[0].parentAutoRuleComponentId == searchParentId) {
        let comp = null
        
        // Note: Child index is list wide. Need to convert it to just children
        if(childIndex > 0) {
          let insertBefore = listCopy[childIndex-1]
          childIndex = outputLevel.findIndex(listComp => listComp.id == insertBefore.id) + 1          
        }

        if(childIndex == -1)
          comp = outputLevel[outputLevel.length-1]
        else
          comp = outputLevel[childIndex-1]

        return {
          availableModelList: comp ? comp.availableModelList : [],
          availablePlaceholderList: comp ? comp.availablePlaceholderList : []
        }
      }

      // If root level & no data exists, return empty struct
      if(parentAutoRuleComponentId == null) {
        return {
          availableModelList: [],
          availablePlaceholderList: []
        }
      }

      return null
    },
    findTreePreviousComponent(componentTreeList, parentAutoRuleComponentId, sublingIndex = -1) {
      // Same level?
      if(componentTreeList.length > 0) {
        if(componentTreeList[0].parentAutoRuleComponentId == parentAutoRuleComponentId) {
          if(sublingIndex == -1)
            return componentTreeList[componentTreeList.length-1]
          return componentTreeList[sublingIndex-1]
        }
      }

      // Go down tree
      for(let comp of componentTreeList) {
        let child = this.findTreePreviousComponent(comp.children, parentAutoRuleComponentId, sublingIndex)
        if(child)
          return child
      }

      // Not found
      return null      
    },
    calculateTreeComponentList(componentId) {
      var outputCompList = []

      var currentComp = this.autoRuleComponents.find(comp => comp.id == componentId)
      if(currentComp) {
        for(let comp of this.autoRuleComponents) {
          if(comp.parentAutoRuleComponentId == currentComp.parentAutoRuleComponentId) {
            outputCompList = _.concat(outputCompList, this.calculateTreeComponentList(comp.parentAutoRuleComponentId))
          }

          if(comp.id == componentId) {
            outputCompList.push(comp)
            break
          }
        }
      }

      return outputCompList
    }

  }
  
}
</script>